import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  query,
  collection,
  onSnapshot,
  DocumentData,
  QueryConstraint,
  updateDoc,
  doc,
} from 'firebase/firestore';
import { BhabiFirestoreModel, Message } from './message';
import { SendMessage, SendMessageProps } from './send-message';
import { db, firebaseAuth } from '@/shared/lib';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useAlert, UserAvatar } from '@/shared/components';
import { MoreVertical, Ban, ShieldCheck, Loader2 } from 'lucide-react';
import { useGetFirebaseUsers } from '../dm';
import { markChatThreadRead } from '../hooks/use-chat-threads';
import { chatMessagesView } from '../lib/chat-view';
import { useMediaQuery, Theme } from '@mui/material';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { blockUser, unblockUser, useIsBlocked } from '../hooks';
import { LoggerService } from '@/shared/logger';
import '../../components/social.css';

interface ChatBoxProps {
  queryConstraint?: QueryConstraint[];
  sendMessageProps?: Omit<SendMessageProps, 'scroll' | 'disabledReason'>;
  outerBoxProps?: React.HTMLAttributes<HTMLDivElement>;
}

export const useFirestoreQuery = (queryConstraint: QueryConstraint[]) => {
  const [messages, setMessages] = useState<DocumentData[]>([]);
  const [loading, setLoading] = useState(queryConstraint.length > 0);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!queryConstraint.length) {
      setMessages([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const q = query(collection(db, 'chat'), ...queryConstraint);

    const unsubscribe = onSnapshot(
      q,
      QuerySnapshot => {
        const fetchedMessages: DocumentData[] = [];
        QuerySnapshot.forEach(doc => {
          fetchedMessages.push({ ...doc.data(), id: doc.id });
        });
        const sortedMessages = fetchedMessages.sort(
          (a, b) => a.createdAt - b.createdAt
        );
        setMessages(sortedMessages);
        setLoading(false);
      },
      err => {
        LoggerService.error('chat listener failed', err);
        setError(err instanceof Error ? err : new Error('Chat unavailable'));
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [queryConstraint]);

  return { messages, loading, error };
};

export const ChatBox: FC<ChatBoxProps> = ({
  queryConstraint = [],
  sendMessageProps,
  outerBoxProps,
}) => {
  const scroll = useRef<HTMLSpanElement>(null);
  const {
    messages,
    loading: messagesLoading,
    error: messagesError,
  } = useFirestoreQuery(queryConstraint);
  const [user] = useAuthState(firebaseAuth);

  const receiverID = sendMessageProps?.receiverID || '';
  const isDM = !!receiverID;
  const {
    iBlocked,
    blockedMe,
    loading: blockLoading,
  } = useIsBlocked(isDM ? receiverID : null);

  // Fetch the receiver's display info so the empty-state banner and the
  // mobile in-chat header can show a friendly name + avatar instead of just
  // "Unknown".
  const { mutate: fetchReceiver, data: receiverData } = useGetFirebaseUsers();
  useEffect(() => {
    if (!isDM) return;
    fetchReceiver([receiverID]);
  }, [isDM, receiverID, fetchReceiver]);
  const receiver = receiverData?.[0];
  const receiverName = receiver?.name || 'this user';

  const isMobile = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!user?.uid) {
      return;
    }

    (messages as BhabiFirestoreModel[]).forEach(message => {
      if (message?.isRead === 'false' && message?.senderID !== user.uid) {
        (async function () {
          await updateDoc(doc(db, 'chat', message.id), 'isRead', 'true');
        })();
      }
    });
  }, [messages, user?.uid]);

  // Mark the /chat_threads summary as read when the user is actively
  // viewing this DM. This is what the sidebar's unread dot keys off.
  useEffect(() => {
    if (!isDM || !user?.uid || !receiverID) return;
    if (!messages.length) return;
    markChatThreadRead(user.uid, receiverID);
  }, [isDM, user?.uid, receiverID, messages.length]);

  // No explicit scrollIntoView needed — with flex-col-reverse the new
  // (first-in-reverse-order) message always renders at the visual bottom,
  // and the scroller stays anchored unless the user has scrolled up to
  // read history. That's exactly what a chat surface wants.

  const disabledReason: SendMessageProps['disabledReason'] = useMemo(() => {
    if (!isDM) return null;
    if (iBlocked) return 'i-blocked';
    if (blockedMe) return 'blocked-me';
    return null;
  }, [isDM, iBlocked, blockedMe]);

  const { className: outerClassName, ...restOuterProps } = outerBoxProps ?? {};

  return (
    <div
      className={[
        'bh-chat flex flex-col h-full min-h-0 w-full min-w-0 overflow-hidden',
        outerClassName ?? '',
      ]
        .join(' ')
        .trim()}
      {...restOuterProps}
    >
      {/* Chat header — only on DMs. Hosts the block / unblock menu, plus
          a mobile-only back button + avatar + name strip so users always
          know who they are talking to without relying on the global top bar. */}
      {isDM && user?.uid && (
        <ChatHeader
          currentUID={user.uid}
          otherUID={receiverID}
          iBlocked={iBlocked}
          blockLoading={blockLoading}
          showIdentity={isMobile}
          receiverName={receiver?.name}
          receiverPhoto={receiver?.photoURL ?? null}
          receiverState={receiver?.state}
        />
      )}

      {/* Banner shown when *I* have blocked this user. */}
      {isDM && iBlocked && user?.uid && (
        <BlockedBanner
          tone="i-blocked"
          onUnblock={() => unblockUser(user.uid, receiverID)}
        />
      )}
      {/* Banner shown when the *other* user has blocked me. */}
      {isDM && !iBlocked && blockedMe && <BlockedBanner tone="blocked-me" />}

      {/* The message list uses flex-col-reverse so the latest message
          sits at the bottom and scrolling up reveals older history. This
          is the standard "chat" pattern — `overflow-y-auto` on a normal
          flex column with justify-end never scrolls correctly because the
          overflowing top items get clipped above the viewport. With
          col-reverse, scrollTop=0 IS the bottom, new messages naturally
          appear in view, and the scroll position is intuitively
          preserved when the user has scrolled up. */}
      <div
        className="bh-chat__log flex-1 overflow-y-auto overflow-x-hidden flex flex-col-reverse w-full min-w-0"
        role="log"
        aria-label={isDM ? `Messages with ${receiverName}` : 'Table chat'}
      >
        {chatMessagesView({
          loading: messagesLoading,
          error: messagesError,
          count: messages.length,
        }) === 'error' ? (
          <div className="bh-chat__empty" role="alert">
            <div className="bh-chat__empty-inner">
              <div className="bh-chat__empty-icon" aria-hidden="true">
                !
              </div>
              <h3 className="bh-h3 bh-chat__empty-title">
                Messages unavailable
              </h3>
              <p className="bh-muted bh-small">
                Check your connection and reopen this conversation.
              </p>
            </div>
          </div>
        ) : chatMessagesView({
            loading: messagesLoading,
            error: messagesError,
            count: messages.length,
          }) === 'loading' ? (
          <div
            className="bh-chat__loading"
            role="status"
            aria-label="Loading messages"
          >
            <span />
            <span />
            <span />
          </div>
        ) : chatMessagesView({
            loading: messagesLoading,
            error: messagesError,
            count: messages.length,
          }) === 'messages' ? (
          [...messages]
            .reverse()
            .map(message => (
              <Message
                key={message.id}
                message={message as BhabiFirestoreModel}
              />
            ))
        ) : (
          <div className="bh-chat__empty">
            <div className="bh-chat__empty-inner">
              {isDM && receiver?.photoURL ? (
                <div className="mx-auto mb-4 inline-block">
                  <UserAvatar
                    hideState
                    photoURL={receiver.photoURL}
                    name={receiver.name}
                    avatarProps={{ sx: { width: 64, height: 64 } }}
                  />
                </div>
              ) : (
                <div className="bh-chat__empty-icon" aria-hidden="true">
                  ♠
                </div>
              )}
              <h3 className="bh-h3 bh-chat__empty-title">
                {isDM
                  ? `Start a conversation with ${receiverName}`
                  : 'Say hello to your match'}
              </h3>
              <p className="bh-muted bh-small">
                {isDM
                  ? 'Your first message will appear right here.'
                  : 'Group chat is visible to everyone in this room.'}
              </p>
            </div>
          </div>
        )}
      </div>
      <SendMessage
        {...sendMessageProps}
        scroll={scroll}
        disabledReason={disabledReason}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Header with the kebab menu (Block / Unblock).
// ─────────────────────────────────────────────────────────────────────────────

interface ChatHeaderProps {
  currentUID: string;
  otherUID: string;
  iBlocked: boolean;
  blockLoading: boolean;
  showIdentity?: boolean;
  receiverName?: string | null;
  receiverPhoto?: string | null;
  receiverState?: 'online' | 'offline' | 'joined' | 'rejoined' | 'left';
}

const ChatHeader: FC<ChatHeaderProps> = ({
  currentUID,
  otherUID,
  iBlocked,
  blockLoading,
  showIdentity = false,
  receiverName,
  receiverPhoto,
  receiverState,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { addAlert } = useAlert();

  const handleBlock = useCallback(async () => {
    setBusy(true);
    try {
      await blockUser(currentUID, otherUID);
      addAlert({
        type: 'success',
        message: 'User blocked. They can no longer message you.',
      });
      setConfirmOpen(false);
    } catch (err) {
      LoggerService.error('blockUser failed', err);
      addAlert({ type: 'error', message: 'Could not block user. Try again.' });
    } finally {
      setBusy(false);
    }
  }, [addAlert, currentUID, otherUID]);

  const handleUnblock = useCallback(async () => {
    setBusy(true);
    try {
      await unblockUser(currentUID, otherUID);
      addAlert({ type: 'success', message: 'User unblocked.' });
    } catch (err) {
      LoggerService.error('unblockUser failed', err);
      addAlert({
        type: 'error',
        message: 'Could not unblock user. Try again.',
      });
    } finally {
      setBusy(false);
      setPopoverOpen(false);
    }
  }, [addAlert, currentUID, otherUID]);

  return (
    <>
      <div className="bh-chat__head">
        {showIdentity && (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <UserAvatar
              hideState={false}
              userState={receiverState}
              photoURL={receiverPhoto ?? null}
              name={receiverName || 'User'}
              avatarProps={{ sx: { width: 32, height: 32 } }}
            />
            <div className="min-w-0 leading-tight">
              <p className="bh-chat__peer" title={receiverName || 'User'}>
                {receiverName || 'User'}
              </p>
              {receiverState && (
                <p className="bh-chat__peer-state" data-state={receiverState}>
                  {receiverState}
                </p>
              )}
            </div>
          </div>
        )}
        {!showIdentity && <div className="flex-1" />}
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Chat options"
              aria-busy={blockLoading}
              className="bh-icon-btn bh-chat__menu-btn"
            >
              {blockLoading ? (
                <Loader2
                  size={18}
                  className="animate-spin"
                  aria-hidden="true"
                />
              ) : (
                <MoreVertical size={18} aria-hidden="true" />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            sideOffset={6}
            className="bh-chat__menu w-52 p-1"
          >
            {iBlocked ? (
              <button
                type="button"
                disabled={busy}
                onClick={handleUnblock}
                className="bh-chat__menu-item"
                data-tone="safe"
              >
                <ShieldCheck size={16} aria-hidden="true" />
                Unblock user
              </button>
            ) : (
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setPopoverOpen(false);
                  setConfirmOpen(true);
                }}
                className="bh-chat__menu-item"
                data-tone="danger"
              >
                <Ban size={16} aria-hidden="true" />
                Block user
              </button>
            )}
          </PopoverContent>
        </Popover>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="bh-chat__danger-title flex items-center gap-2">
              <Ban size={18} aria-hidden="true" />
              Block this user?
            </DialogTitle>
            <DialogDescription>
              They won&apos;t be able to send you any new messages, and you
              won&apos;t be able to message them until you unblock. You can undo
              this at any time from the chat menu.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleBlock} disabled={busy}>
              {busy ? 'Blocking…' : 'Block'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Inline banner shown above the message list when the thread is blocked.
// ─────────────────────────────────────────────────────────────────────────────

const BlockedBanner: FC<{
  tone: 'i-blocked' | 'blocked-me';
  onUnblock?: () => void | Promise<void>;
}> = ({ tone, onUnblock }) => {
  const isMine = tone === 'i-blocked';
  return (
    <div className="bh-chat__blocked" data-tone={tone} role="status">
      <Ban size={16} aria-hidden="true" className="bh-chat__blocked-icon" />
      <span className="flex-1">
        {isMine
          ? 'You have blocked this user. They cannot send you messages.'
          : 'You can no longer reply to this conversation.'}
      </span>
      {isMine && onUnblock && (
        <button
          type="button"
          onClick={() => onUnblock()}
          className="bh-btn bh-btn--secondary bh-btn--small"
        >
          <ShieldCheck size={15} aria-hidden="true" />
          Unblock
        </button>
      )}
    </div>
  );
};
